import { useState, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { v4 as UUID } from 'uuid';
import { Activity } from '../../../Model/Activity/Activity';
import { ActivityLog, newActivityLog } from '../../../Model/Activity/ActivityLog';
import { BaseTrackingKey } from '../../../Model/ProgramPortfolio/TrackingKeys';

export type UseActivityLogFormResult = {
  activityLog: ActivityLog | null;
  selectedActivity: Activity | null;
  date: string;
  startTime: string;
  endTime: string;
  trackingKeysToShow: BaseTrackingKey[];
  setDate: (date: string) => void;
  setStartTime: (time: string) => void;
  setEndTime: (time: string) => void;
  setTrackingKeyValue: (key: BaseTrackingKey, value: number) => void;
  createLogForActivity: (activity: Activity) => void;
  reset: () => void;
};

export function useActivityLogForm(): UseActivityLogFormResult {
  const [activityLog, setActivityLog] = useState<ActivityLog | null>(null);
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [startTime, setStartTime] = useState(dayjs().subtract(30, 'minute').format('HH:mm'));
  const [endTime, setEndTime] = useState(dayjs().format('HH:mm'));

  const withUpdatedDateTime = useCallback(
    (log: ActivityLog, nextDate: string, nextStartTime: string, nextEndTime: string): ActivityLog => {
      const startDateTime = dayjs(`${nextDate} ${nextStartTime}`);
      const endDateTime = dayjs(`${nextDate} ${nextEndTime}`);
      const durationMinutes = endDateTime.diff(startDateTime, 'minute');

      return {
        ...log,
        startDate: startDateTime.toISOString(),
        endDate: endDateTime.toISOString(),
        data: { ...log.data, DURATION: Math.max(0, durationMinutes * 60) },
      };
    },
    [],
  );

  const createLogForActivity = useCallback(
    (activity: Activity) => {
      setSelectedActivity(activity);

      const startDateTime = dayjs(`${date} ${startTime}`);
      const endDateTime = dayjs(`${date} ${endTime}`);
      const durationMinutes = endDateTime.diff(startDateTime, 'minute');

      const newLog = newActivityLog({
        id: UUID(),
        type: activity.identifier,
        activityId: activity.id,
        startDate: startDateTime.toISOString(),
        endDate: endDateTime.toISOString(),
        data: { DURATION: Math.max(0, durationMinutes * 60) },
        activity,
      });
      setActivityLog(newLog);
    },
    [date, startTime, endTime],
  );

  const handleSetDate = useCallback(
    (nextDate: string) => {
      setDate(nextDate);
      setActivityLog((prev) => (prev ? withUpdatedDateTime(prev, nextDate, startTime, endTime) : prev));
    },
    [endTime, startTime, withUpdatedDateTime],
  );

  const handleSetStartTime = useCallback(
    (nextStartTime: string) => {
      setStartTime(nextStartTime);
      setActivityLog((prev) => (prev ? withUpdatedDateTime(prev, date, nextStartTime, endTime) : prev));
    },
    [date, endTime, withUpdatedDateTime],
  );

  const handleSetEndTime = useCallback(
    (nextEndTime: string) => {
      setEndTime(nextEndTime);
      setActivityLog((prev) => (prev ? withUpdatedDateTime(prev, date, startTime, nextEndTime) : prev));
    },
    [date, startTime, withUpdatedDateTime],
  );

  const setTrackingKeyValue = useCallback(
    (key: BaseTrackingKey, value: number) => {
      setActivityLog((prev) => {
        if (!prev) return prev;
        return { ...prev, data: { ...prev.data, [key]: value } };
      });
    },
    [],
  );

  const trackingKeysToShow = useMemo(() => {
    if (!selectedActivity) return [];
    const keys = [...selectedActivity.trackingKeysSorted];
    if (!keys.includes('STEPS')) {
      keys.push('STEPS');
    }
    return keys.filter((k) => k !== 'DURATION');
  }, [selectedActivity]);

  const reset = useCallback(() => {
    setActivityLog(null);
    setSelectedActivity(null);
    setDate(dayjs().format('YYYY-MM-DD'));
    setStartTime(dayjs().subtract(30, 'minute').format('HH:mm'));
    setEndTime(dayjs().format('HH:mm'));
  }, []);

  return {
    activityLog,
    selectedActivity,
    date,
    startTime,
    endTime,
    trackingKeysToShow,
    setDate: handleSetDate,
    setStartTime: handleSetStartTime,
    setEndTime: handleSetEndTime,
    setTrackingKeyValue,
    createLogForActivity,
    reset,
  };
}
